'use client';

import React, { memo, useState, Suspense, lazy, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Package, Droplets, Shield, Recycle, ArrowRight, Star, Info, ShieldCheck } from 'lucide-react';
import Image from 'next/image';
import { Button, Badge, LoadingSpinner } from '@/components/ui/design-system';

// Lazy load the 3D viewer for better performance
const Product3DViewer = lazy(() => import('./Product3DViewer'));

interface ProductsSectionProps {
  onProductSelect?: (product: 'fridgeBottle' | 'makhanaJar') => void;
}
// Model paths for demonstration
const MODEL_PATHS = {
  fridgeBottle: '/models/fridge-bottles.glb',
  makhanaJar: '/models/makhana-pet-can-jars.glb', // Use the same model until makhana model is available
};

// Image fallbacks when 3D models aren't available
// Optimized with proper sizes and priority for better Core Web Vitals
const IMAGE_FALLBACKS = {
  fridgeBottle: {
    src: '/images/pages/products/pet-bottles/fridge-bottles.jpg',
    width: 800,
    height: 600,
    alt: 'Crystal Clear Fridge Bottle - Premium BPA-free packaging with airtight seal',
    blurDataURL: 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wCEAAEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAf/AABEIAAYACgMBEQACEQEDEQH/xAGiAAABBQEBAQEBAQAAAAAAAAAAAQIDBAUGBwgJCgsQAAIBAwMCBAMFBQQEAAABfQECAwAEEQUSITFBBhNRYQcicRQygZGhCCNCscEVUtHwJDNicoIJChYXGBkaJSYnKCkqNDU2Nzg5OkNERUZHSElKU1RVVldYWVpjZGVmZ2hpanN0dXZ3eHl6g4SFhoeIiYqSk5SVlpeYmZqio6Slpqeoqaqys7S1tre4ubrCw8TFxsfIycrS09TV1tfY2drh4uPk5ebn6Onq8fLz9PX29/j5+gEAAwEBAQEBAQEBAQAAAAAAAAECAwQFBgcICQoLEQACAQIEBAMEBwUEBAABAncAAQIDEQQFITEGEkBRB2FxEyIygQgUQpGhscEJIzNS8BVictEKFiQ04SXxFxgZGiYnKCkqNTY3ODk6Q0RFRkdISUpTVFVWV1hZWmNkZWZnaGlqc3R1dnd4eXqCg4SFhoeIiYqSk5SVlpeYmZqio6Slpqeoqaqys7S1tre4ubrCw8TFxsfIycrS09TV1tfY2dri4+Tl5ufo6ery8/T19vf4+fr/2gAMAwEAAhEDEQA/AP5YdM8PWf2azOrS3OprZg6hJ9rtLENdFN19PpkOm2/2izEGo+XHqOrXcuqPHJeT2a2kc9rpthJcX/pGJx+Mnhq9Z4ChFppxgnjZpr3U4tuOXw9oxa1Um0tVzXs1+dZPl/DuKzLLaFfNcZKKb53h8FSi3JxhNNPEYui0pSlZOzinFtaOSb+Jry9vLC4bTJfDlpJp17aadBNqXhjS5r25XTY4vOu5LqSV3ubm4uZZbi6u7iWWe4uJpZ55pZpZHdz5EY1k1KM5OMXN2vZq97K6d9N7a6aLZdPpJxrRXJFyjzN2TaSbcm5O7bXvO7tfXRaaLQY3iPzzTyhVlmC2ybQvlpbJOoVVUHBzIGxlgGIySO1YrCYSKsqEF6X/APkn+REsTVbvKcr/ANf16n//2Q==',
    loading: 'eager', // Load this image immediately as it's above the fold
    fetchPriority: 'high'
  },
  makhanaJar: {
    src: '/images/pages/products/pet-jars/makhana-jars.jpg',
    width: 800,
    height: 600,
    alt: 'Premium Makhana Pet Can Jar - Air-tight seal technology for food storage',
    blurDataURL: 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wCEAAEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAf/AABEIAAYACgMBEQACEQEDEQH/xAGiAAABBQEBAQEBAQAAAAAAAAAAAQIDBAUGBwgJCgsQAAIBAwMCBAMFBQQEAAABfQECAwAEEQUSITFBBhNRYQcicRQygZGhCCNCscEVUtHwJDNicoIJChYXGBkaJSYnKCkqNDU2Nzg5OkNERUZHSElKU1RVVldYWVpjZGVmZ2hpanN0dXZ3eHl6g4SFhoeIiYqSk5SVlpeYmZqio6Slpqeoqaqys7S1tre4ubrCw8TFxsfIycrS09TV1tfY2drh4uPk5ebn6Onq8fLz9PX29/j5+gEAAwEBAQEBAQEBAQAAAAAAAAECAwQFBgcICQoLEQACAQIEBAMEBwUEBAABAncAAQIDEQQFITEGEkBRB2FxEyIygQgUQpGhscEJIzNS8BVictEKFiQ04SXxFxgZGiYnKCkqNTY3ODk6Q0RFRkdISUpTVFVWV1hZWmNkZWZnaGlqc3R1dnd4eXqCg4SFhoeIiYqSk5SVlpeYmZqio6Slpqeoqaqys7S1tre4ubrCw8TFxsfIycrS09TV1tfY2dri4+Tl5ufo6ery8/T19vf4+fr/2gAMAwEAAhEDEQA/AP5YdM8PWf2azOrS3OprZg6hJ9rtLENdFN19PpkOm2/2izEGo+XHqOrXcuqPHJeT2a2kc9rpthJcX/pGJx+Mnhq9Z4ChFppxgnjZpr3U4tuOXw9oxa1Um0tVzXs1+dZPl/DuKzLLaFfNcZKKb53h8FSi3JxhNNPEYui0pSlZOzinFtaOSb+Jry9vLC4bTJfDlpJp17aadBNqXhjS5r25XTY4vOu5LqSV3ubm4uZZbi6u7iWWe4uJpZ55pZpZHdz5EY1k1KM5OMXN2vZq97K6d9N7a6aLZdPpJxrRXJFyjzN2TaSbcm5O7bXvO7tfXRaaLQY3iPzzTyhVlmC2ybQvlpbJOoVVUHBzIGxlgGIySO1YrCYSKsqEF6X/APkn+REsTVbvKcr/ANf16n//2Q==',
    loading: 'eager', // Load this image immediately as it's above the fold
    fetchPriority: 'high'
  }
};

// Loading fallback component styled to match the design with proper accessibility
const ProductViewerPlaceholder = memo(({ productType }: { productType: 'fridgeBottle' | 'makhanaJar' }) => (
  <div 
    className="h-full w-full flex items-center justify-center bg-gray-50 dark:bg-gray-700 rounded-xl p-4"
    role="status"
    aria-live="polite"
    aria-busy="true"
  >
    <div className="flex flex-col items-center">
      <div 
        className="animate-pulse w-16 h-16 bg-gray-200 dark:bg-gray-600 rounded-full mb-3"
        aria-hidden="true"
      ></div>
      <div className="text-sm text-gray-500 dark:text-gray-400 text-center">
        {productType === 'fridgeBottle' ? 'Loading Fridge Bottle...' : 'Loading Makhana Jar...'}
      </div>
    </div>
  </div>
));

ProductViewerPlaceholder.displayName = 'ProductViewerPlaceholder';

// Memoized InfoTooltip component
const InfoTooltip = memo(() => (
  <div className="relative group/info hidden md:block">
    <button aria-label="Show information" className="info-button flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-800/30 transition-colors z-30 relative">
      <Info className="h-4 w-4 text-gray-600 dark:text-gray-300 group-hover/info:text-red-500 dark:group-hover/info:text-red-400" />
    </button>
    <div role="tooltip" className="info-tooltip w-64 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-lg text-sm text-gray-600 dark:text-gray-300 right-0 top-full mt-2">
      <div className="flex items-start mb-2">
        <ShieldCheck className="h-4 w-4 text-red-500 dark:text-red-400 mr-2 mt-0.5 flex-shrink-0" />
        <span>BPA-Free Food-Grade Materials</span>
      </div>
      <div className="flex items-start mb-2">
        <ShieldCheck className="h-4 w-4 text-red-500 dark:text-red-400 mr-2 mt-0.5 flex-shrink-0" />
        <span>Leak-Proof Double-Cap Design</span>
      </div>
      <div className="flex items-start">
        <ShieldCheck className="h-4 w-4 text-red-500 dark:text-red-400 mr-2 mt-0.5 flex-shrink-0" />
        <span>Dishwasher Safe & Easy to Clean</span>
      </div>
    </div>
  </div>
));

InfoTooltip.displayName = 'InfoTooltip';

// Memoized MakhanaInfoTooltip component
const MakhanaInfoTooltip = memo(() => (
  <div className="relative group/info hidden md:block">
    <button aria-label="Show information" className="info-button flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-800/30 transition-colors z-30 relative">
      <Info className="h-4 w-4 text-gray-600 dark:text-gray-300 group-hover/info:text-red-500 dark:group-hover/info:text-red-400" />
    </button>
    <div role="tooltip" className="info-tooltip w-64 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-lg text-sm text-gray-600 dark:text-gray-300 right-0 top-full mt-2">
      <div className="flex items-start mb-2">
        <ShieldCheck className="h-4 w-4 text-red-500 dark:text-red-400 mr-2 mt-0.5 flex-shrink-0" />
        <span>100% Air-Tight Seal Technology</span>
      </div>
      <div className="flex items-start mb-2">
        <ShieldCheck className="h-4 w-4 text-red-500 dark:text-red-400 mr-2 mt-0.5 flex-shrink-0" />
        <span>Transparent Food-Grade PET Material</span>
      </div>
      <div className="flex items-start">
        <ShieldCheck className="h-4 w-4 text-red-500 dark:text-red-400 mr-2 mt-0.5 flex-shrink-0" />
        <span>Stackable Design for Efficient Storage</span>
      </div>
    </div>
  </div>
));

MakhanaInfoTooltip.displayName = 'MakhanaInfoTooltip';

// Memoized FeatureList component
const FeatureList = memo(({ features }: { features: string[] }) => (
  <div className="md:hidden space-y-3 mb-4 bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Key Features:</h4>
    {features.map((feature, index) => (
      <div key={index} className="flex items-start">
        <ShieldCheck className="h-4 w-4 text-red-500 dark:text-red-400 mr-2 mt-0.5 flex-shrink-0" />
        <span className="text-sm text-gray-600 dark:text-gray-300">{feature}</span>
      </div>
    ))}
        </div>
));

FeatureList.displayName = 'FeatureList';
        
// Memoized ProductCard component
const FridgeBottleCard = memo(({ onProductSelect }: { onProductSelect: (product: 'fridgeBottle') => void }) => (
          <motion.div 
            key="fridge-bottle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative group"
          >
            <div className="p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg h-full transform transition-transform duration-300 group-hover:shadow-xl group-hover:-translate-y-1 relative overflow-hidden">
              <div className="flex md:flex-row flex-col gap-6">
                <div className="md:w-1/2 flex-shrink-0">
                  <div className="h-64 relative overflow-hidden rounded-xl bg-gray-50 dark:bg-gray-700">
            {/* Lazy loaded Product Viewer with fallback */}
            <Suspense fallback={<ProductViewerPlaceholder productType="fridgeBottle" />}>
                    <Product3DViewer
                      modelPath={MODEL_PATHS.fridgeBottle}
                      imageFallback={IMAGE_FALLBACKS.fridgeBottle}
                      height="100%"
                      width="100%"
                      badgeText="Crystal Clear"
                      badgeVariant="premium"
                      spotlight
                      productName="Crystal Clear Fridge Bottle"
                      productDescription="Premium glass-like clarity with airtight seal"
                      info="Our fridge bottles feature exceptional clarity and transparency, providing a crystal-clear view of contents while maintaining premium quality and durability."
                      enableZoom
                      modelScale={2}
                      radius="md"
                      variant="glass"
                      float
                      forceImageFallback={false}
                    />
            </Suspense>
                  </div>
                </div>
                
                <div className="md:w-1/2">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
                      Fridge Bottles
                    </h3>
            <InfoTooltip />
                  </div>
                    
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                    Engineered for refrigerator door storage with crystal-clear view of contents. Available in various sizes with customizable colors and caps. Perfect for keeping your beverages fresh and your fridge organized.
                  </p>

                  {/* Mobile-only feature list */}
          <FeatureList features={[
            "BPA-Free Food-Grade Materials",
            "Leak-Proof Double-Cap Design",
            "Dishwasher Safe & Easy to Clean"
          ]} />
                    
                  <div className="mt-auto">
                    <Button
                      variant="primary"
                      size="md"
                      asChild
                    >
                      <a
                        href="#contact-form"
                        className="inline-flex items-center"
                        onClick={(e) => {
                          e.preventDefault();
                          onProductSelect('fridgeBottle');
                        }}
                      >
                        <span>Inquire Now</span>
                        <ArrowRight className="ml-2 h-3.5 w-3.5" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
));

FridgeBottleCard.displayName = 'FridgeBottleCard';
          
// Memoized MakhanaJarCard component
const MakhanaJarCard = memo(({ onProductSelect }: { onProductSelect: (product: 'makhanaJar') => void }) => (
          <motion.div 
            key="makhana-jar"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative group"
          >
            <div className="p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg h-full transform transition-transform duration-300 group-hover:shadow-xl group-hover:-translate-y-1 relative overflow-hidden">
              <div className="flex md:flex-row flex-col gap-6">
                <div className="md:w-1/2 flex-shrink-0">
                  <div className="h-64 relative overflow-hidden rounded-xl bg-gray-50 dark:bg-gray-700">
            {/* Lazy loaded Product Viewer with fallback */}
            <Suspense fallback={<ProductViewerPlaceholder productType="makhanaJar" />}>
                    <Product3DViewer
                      modelPath={MODEL_PATHS.makhanaJar}
                      imageFallback={IMAGE_FALLBACKS.makhanaJar}
                      height="100%"
                      width="100%"
                      badgeText="Air-Tight"
                      badgeVariant="premium"
                      spotlight
                      productName="Premium Makhana Pet Can Jar"
                      productDescription="Perfect for storing snacks and dry goods"
                      info="Our makhana jars feature an air-tight seal to keep contents fresh for longer periods."
                      enableZoom
                      modelScale={2}
                      radius="md"
                      variant="glass"
                      float
                      forceImageFallback={false}
                    />
            </Suspense>
                  </div>
                </div>
                
                <div className="md:w-1/2">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
                      Makhana Pet Can Jars
                    </h3>
            <MakhanaInfoTooltip />
                  </div>
                    
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                    Ideal for preserving snacks and dry goods with air-tight seal technology. Perfect for makhana, dry fruits, and other snacks with customizable labeling options.
                  </p>

                  {/* Mobile-only feature list */}
          <FeatureList features={[
            "100% Air-Tight Seal Technology",
            "Transparent Food-Grade PET Material",
            "Stackable Design for Efficient Storage"
          ]} />
                    
                  <div className="mt-auto">
                    <Button
                      variant="primary"
                      size="md"
                      asChild
                    >
                      <a
                        href="#contact-form"
                        className="inline-flex items-center"
                        onClick={(e) => {
                          e.preventDefault();
                          onProductSelect('makhanaJar');
                        }}
                      >
                        <span>Inquire Now</span>
                        <ArrowRight className="ml-2 h-3.5 w-3.5" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
));

MakhanaJarCard.displayName = 'MakhanaJarCard';


/**
 * Products Section Component
 * 
 * Displays featured products with proper semantic HTML structure,
 * accessibility features, and responsive design following design system patterns.
 */
const ProductsSection: React.FC<ProductsSectionProps> = memo(({ onProductSelect }) => {
  const handleProductSelect = useCallback((product: 'fridgeBottle' | 'makhanaJar') => {
    onProductSelect?.(product);
  }, [onProductSelect]);

  // Add structured data for products (helps with SEO)
  const structuredData = {
    "@context": "https://schema.org/",
    "@type": "ItemList",
    "itemListElement": [
      {
        "@type": "Product",
        "name": "Crystal Clear Fridge Bottle",
        "description": "Premium glass-like clarity with airtight seal. BPA-Free Food-Grade Materials.",
        "image": IMAGE_FALLBACKS.fridgeBottle.src
      },
      {
        "@type": "Product",
        "name": "Premium Makhana Pet Can Jar",
        "description": "Perfect for storing snacks and dry goods with air-tight seal technology.",
        "image": IMAGE_FALLBACKS.makhanaJar.src
      }
    ]
  };

  return (
    <section 
      id="featured-products" 
      className="py-12 md:py-16 lg:py-20 bg-white dark:bg-gray-900 scroll-mt-16"
      aria-labelledby="products-heading"
      role="region"
    >
      {/* Add structured data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <header className="mb-10 lg:mb-12 text-center">
          <h2 
            id="products-heading"
            className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4"
          >
            Featured Products
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Discover our premium BPA-free packaging innovations that blend sustainability with sophisticated design. Elevate your products with containers that protect, preserve, and impress.
          </p>
        </header>
        
        <div 
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12"
          role="list"
          aria-label="Product showcase"
        >
          {/* Fridge Bottle */}
          <div role="listitem">
            <FridgeBottleCard onProductSelect={handleProductSelect} />
          </div>
          
          {/* Makhana Jar */}
          <div role="listitem">
            <MakhanaJarCard onProductSelect={handleProductSelect} />
          </div>
        </div>
      </div>
    </section>
  );
});

export default memo(ProductsSection);
